import React, { useState, useEffect, useContext } from "react";
import { makeStyles, useTheme } from "@material-ui/core/styles";
import {
  Typography,
  CircularProgress,
  Chip,
  IconButton,
  Tooltip,
} from "@material-ui/core";
import OpenInNewIcon from "@material-ui/icons/OpenInNew";
import RefreshIcon from "@material-ui/icons/Refresh";
import HelpOutlineIcon from "@material-ui/icons/HelpOutline";
import ZoomInIcon from "@material-ui/icons/ZoomIn";
import ZoomOutIcon from "@material-ui/icons/ZoomOut";
import PhoneIcon from "@material-ui/icons/Phone";
import moment from "moment";
import api from "../../services/api";
import { AuthContext } from "../../context/Auth/AuthContext";
import { socketConnection } from "../../services/socket";
import Board from "react-trello";
import { toast } from "react-toastify";
import { useHistory } from "react-router-dom";

const ZOOM_KEY = "kanbanZoom";
const ZOOM_STEP = 0.1;
const ZOOM_MIN = 0.5;
const ZOOM_MAX = 1.0;
const EMPTY_CARD_PREFIX = "__empty__";

// ---------------------------------------------------------------------------
// Styles
// ---------------------------------------------------------------------------
const useStyles = makeStyles((theme) => ({
  root: {
    display: "flex",
    flexDirection: "column",
    height: "100%",
    overflow: "hidden",
  },

  header: {
    display: "flex",
    alignItems: "center",
    padding: theme.spacing(1.5, 2),
    borderBottom: `1px solid ${theme.palette.divider}`,
    gap: theme.spacing(0.5),
    flexShrink: 0,
  },

  zoomLabel: {
    fontSize: "0.72rem",
    color: theme.palette.text.secondary,
    minWidth: 34,
    textAlign: "center",
    userSelect: "none",
  },

  boardWrapper: {
    flex: 1,
    overflow: "hidden",
    position: "relative",
    "& *::-webkit-scrollbar": {
      width: 4,
      height: 4,
    },
    "& *::-webkit-scrollbar-thumb": {
      borderRadius: 4,
      backgroundColor: theme.palette.type === "dark"
        ? "rgba(255,255,255,0.15)"
        : "rgba(0,0,0,0.2)",
    },
    "& *::-webkit-scrollbar-track": {
      background: "transparent",
    },
  },

  boardScaler: {
    transformOrigin: "top left",
    height: "100%",
  },

  loadingOverlay: {
    position: "absolute",
    inset: 0,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: theme.palette.type === "dark"
      ? "rgba(0,0,0,0.5)"
      : "rgba(255,255,255,0.7)",
    zIndex: 10,
  },

  // ── Card ──
  card: {
    backgroundColor: theme.palette.background.paper,
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 10,
    padding: "10px 12px",
    marginBottom: 8,
    cursor: "grab",
    boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
    "&:hover": {
      boxShadow: "0 3px 8px rgba(0,0,0,0.14)",
    },
    transition: "box-shadow 0.15s ease",
    width: 240,
    minWidth: 240,
    maxWidth: 240,
    boxSizing: "border-box",
    overflow: "hidden",
  },

  emptyCard: {
    width: 240,
    minWidth: 240,
    maxWidth: 240,
    padding: "20px 0",
    textAlign: "center",
    fontSize: "0.75rem",
    color: theme.palette.text.disabled,
    fontStyle: "italic",
    userSelect: "none",
    cursor: "default",
  },

  cardHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 4,
  },

  cardName: {
    fontWeight: 600,
    fontSize: "0.82rem",
    color: theme.palette.text.primary,
    flex: 1,
    minWidth: 0,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },

  cardInfo: {
    display: "flex",
    alignItems: "center",
    gap: 4,
    marginBottom: 4,
  },

  cardPhone: {
    fontSize: "0.72rem",
    color: theme.palette.text.disabled,
    lineHeight: 1,
  },

  cardMessage: {
    fontSize: "0.73rem",
    color: theme.palette.text.secondary,
    overflow: "hidden",
    display: "-webkit-box",
    "-webkit-line-clamp": 2,
    "-webkit-box-orient": "vertical",
    marginBottom: 6,
    lineHeight: "1.4",
    maxHeight: "2.8em",
  },

  cardFooter: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    marginTop: 6,
    borderTop: `1px solid ${theme.palette.divider}`,
    paddingTop: 6,
  },

  cardMeta: {
    display: "flex",
    alignItems: "center",
    gap: 6,
  },

  cardTime: {
    fontSize: "0.65rem",
    color: theme.palette.text.disabled,
  },

  ticketChip: {
    height: 18,
    fontSize: "0.65rem",
    borderRadius: 6,
    backgroundColor: theme.palette.type === "dark"
      ? "rgba(255,255,255,0.08)"
      : "rgba(0,0,0,0.06)",
    color: theme.palette.text.secondary,
  },

  openIcon: {
    padding: 2,
    color: theme.palette.primary.main,
    flexShrink: 0,
  },

  queueDot: {
    width: 8,
    height: 8,
    borderRadius: "50%",
    flexShrink: 0,
    display: "inline-block",
  },
}));

// ---------------------------------------------------------------------------
// Custom Card Component
// ---------------------------------------------------------------------------
const KanbanCard = ({ id, title, description, classes, history }) => {
  const { message, uuid, number, queueName, queueColor, updatedAt, isEmpty } = description || {};

  if (isEmpty) {
    return <div className={classes.emptyCard}>Nenhum ticket</div>;
  }

  return (
    <div className={classes.card}>
      <div className={classes.cardHeader}>
        <Typography className={classes.cardName} title={title}>
          {title}
        </Typography>
        <Tooltip title="Abrir ticket">
          <IconButton
            className={classes.openIcon}
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              history.push(`/tickets/${uuid}`);
            }}
          >
            <OpenInNewIcon style={{ fontSize: 14 }} />
          </IconButton>
        </Tooltip>
      </div>

      {number && (
        <div className={classes.cardInfo}>
          <PhoneIcon style={{ fontSize: 11, opacity: 0.4 }} />
          <Typography className={classes.cardPhone}>{number}</Typography>
        </div>
      )}

      {message && (
        <Typography className={classes.cardMessage}>{message}</Typography>
      )}

      <div className={classes.cardFooter}>
        <Chip label={`#${id}`} size="small" className={classes.ticketChip} />
        <div className={classes.cardMeta}>
          {queueName && (
            <Tooltip title={queueName} placement="top">
              <span
                className={classes.queueDot}
                style={{ backgroundColor: queueColor || "#999" }}
              />
            </Tooltip>
          )}
          {updatedAt && (
            <Typography className={classes.cardTime}>
              {moment(updatedAt).fromNow(true)}
            </Typography>
          )}
        </div>
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Kanban Page
// ---------------------------------------------------------------------------
const Kanban = () => {
  const classes = useStyles();
  const theme = useTheme();
  const history = useHistory();
  const { user } = useContext(AuthContext);

  const [tags, setTags] = useState([]);
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [zoom, setZoom] = useState(() => {
    const saved = parseFloat(localStorage.getItem(ZOOM_KEY));
    return !isNaN(saved) ? saved : 1.0;
  });

  const queueIds = user.queues.map((q) => q.UserQueue.queueId);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [tagsRes, ticketsRes] = await Promise.all([
        api.get("/tags/kanban"),
        api.get("/ticket/kanban", {
          params: { queueIds: JSON.stringify(queueIds), teste: true },
        }),
      ]);
      // A API já devolve ordenado por kanbanOrder; o sort local mantém o
      // board correto mesmo se a resposta vier de outra origem, igual ao
      // OpportunityKanban faz com os estágios do pipeline.
      const kanbanTags = [...(tagsRes.data.lista || [])].sort(
        (a, b) => (a.kanbanOrder ?? 0) - (b.kanbanOrder ?? 0)
      );
      setTags(kanbanTags);
      setTickets(ticketsRes.data.tickets || []);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    const companyId = localStorage.getItem("companyId");
    const socket = socketConnection({ companyId });

    socket.on(`company-${companyId}-ticket`, (data) => {
      if (["update", "create", "delete"].includes(data.action)) {
        fetchData();
      }
    });

    // Reordenar as raias na tela de Tags precisa refletir nos boards abertos.
    socket.on(`company-${companyId}-tag`, (data) => {
      if (data.action === "reorder") {
        fetchData();
      }
    });

    return () => socket.disconnect();
  }, []);

  const handleZoom = (delta) => {
    setZoom((prev) => {
      const next = Math.min(ZOOM_MAX, Math.max(ZOOM_MIN, Math.round((prev + delta) * 10) / 10));
      localStorage.setItem(ZOOM_KEY, String(next));
      return next;
    });
  };

  const buildBoard = () => {
    const laneBackground = (color) => {
      if (color) return `${color}22`;
      return theme.palette.type === "dark" ? "#243040" : "#f0f2f5";
    };

    const makeCards = (list, laneId) => {
      if (list.length === 0) {
        return [{
          id: `${EMPTY_CARD_PREFIX}${laneId}`,
          title: "",
          description: { isEmpty: true },
          draggable: false,
        }];
      }
      return list.map((ticket) => ({
        id: `${ticket.id}:${laneId}`,
        title: ticket.contact.name,
        label: `#${ticket.id}`,
        description: {
          message: ticket.lastMessage,
          uuid: ticket.uuid,
          number: ticket.contact.number,
          queueName: ticket.queue?.name,
          queueColor: ticket.queue?.color,
          updatedAt: ticket.updatedAt,
        },
        draggable: true,
      }));
    };

    const kanbanTagIds = new Set(tags.map((t) => Number(t.id)));

    const getTicketLane = (ticket) => {
      for (const tag of (ticket.tags || [])) {
        if (kanbanTagIds.has(Number(tag.id))) return String(tag.id);
      }
      return "open";
    };

    const openTickets = tickets.filter((t) => getTicketLane(t) === "open");

    const lanes = [
      {
        id: "open",
        title: "Sem categoria",
        label: String(openTickets.length),
        style: {
          backgroundColor: laneBackground(null),
          borderRadius: 10,
        },
        titleStyle: { color: theme.palette.text.primary, fontWeight: 700 },
        cards: makeCards(openTickets, "open"),
      },
      ...tags.map((tag) => {
        const laneId = tag.id.toString();
        const tagTickets = tickets.filter((t) => getTicketLane(t) === laneId);
        return {
          id: laneId,
          title: tag.name,
          label: String(tagTickets.length),
          style: {
            backgroundColor: laneBackground(tag.color),
            borderRadius: 10,
            borderTop: `3px solid ${tag.color || "#ccc"}`,
          },
          titleStyle: { color: tag.color || theme.palette.text.primary, fontWeight: 700 },
          cards: makeCards(tagTickets, laneId),
        };
      }),
    ];

    return { lanes };
  };

  const handleCardMove = async (fromLaneId, toLaneId, cardId) => {
    if (cardId.startsWith(EMPTY_CARD_PREFIX)) return;
    const ticketId = cardId.split(":")[0];
    try {
      if (fromLaneId !== "open") {
        await api.delete(`/ticket-tags/${ticketId}`);
      }
      if (toLaneId !== "open") {
        await api.put(`/ticket-tags/${ticketId}/${toLaneId}`);
      }
      fetchData();
    } catch (err) {
      toast.error("Erro ao mover ticket.");
      fetchData();
    }
  };

  const boardData = buildBoard();

  return (
    <div className={classes.root}>
      {/* Header */}
      <div className={classes.header}>
        <Typography variant="h6" style={{ fontWeight: 700, flex: 1, color: theme.palette.text.primary }}>
          Kanban
        </Typography>

        {/* Zoom controls */}
        <Tooltip title="Diminuir zoom">
          <span>
            <IconButton
              size="small"
              onClick={() => handleZoom(-ZOOM_STEP)}
              disabled={zoom <= ZOOM_MIN}
            >
              <ZoomOutIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
        <Typography className={classes.zoomLabel}>{Math.round(zoom * 100)}%</Typography>
        <Tooltip title="Aumentar zoom">
          <span>
            <IconButton
              size="small"
              onClick={() => handleZoom(ZOOM_STEP)}
              disabled={zoom >= ZOOM_MAX}
            >
              <ZoomInIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>

        <Tooltip
          title={
            <span>
              As raias são formadas pelas <strong>tags marcadas como Kanban</strong> nas configurações.
              <br /><br />
              Cada ticket aparece em <strong>apenas uma raia</strong> — a da primeira tag Kanban encontrada.
              Ao arrastar para outra raia, a tag anterior é removida automaticamente.
              <br /><br />
              Tickets exibidos são filtrados pelas <strong>filas do seu usuário</strong>.
            </span>
          }
          placement="bottom-end"
          arrow
        >
          <IconButton size="small" style={{ color: theme.palette.text.secondary }}>
            <HelpOutlineIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        <Tooltip title="Atualizar board">
          <IconButton size="small" onClick={fetchData} disabled={loading}>
            <RefreshIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </div>

      {/* Board */}
      <div className={classes.boardWrapper}>
        {loading && (
          <div className={classes.loadingOverlay}>
            <CircularProgress size={36} />
          </div>
        )}
        <div
          className={classes.boardScaler}
          style={{
            transform: `scale(${zoom})`,
            width: `${(100 / zoom).toFixed(2)}%`,
            height: `${(100 / zoom).toFixed(2)}%`,
          }}
        >
          <Board
            data={boardData}
            onCardMoveAcrossLanes={handleCardMove}
            hideCardDeleteIcon
            style={{
              backgroundColor: "transparent",
              height: "100%",
              padding: "12px 8px",
            }}
            components={{
              Card: (props) => (
                <KanbanCard
                  {...props}
                  classes={classes}
                  history={history}
                />
              ),
            }}
          />
        </div>
      </div>
    </div>
  );
};

export default Kanban;
